import { BARBARIAN, LEGACIES, NIGHTMARES, PALADIN, SKILL_CASTS, type DamageType, type PaladinAura, type SkillDef } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { cosA, dist2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Hero, Structure } from '../types.ts';
import { damageEnemy, enemiesNear, ev, findEnemy, findHero, heroDown, isFlying, targetable } from './combat.ts';
import type { HeroInit } from './create.ts';
import { reference } from './reference.ts';
import { stepToward } from './enemies.ts';
import { blowOf } from './kits.ts';
import { firesAsTower, isBoss, slowFor, stunFor } from './towers.ts';
import { bigFoe } from './gear.ts';
import { kitOf } from './skills.ts';

/**
 * The paths' legacies (docs/SENDAS.md, «El legado»; numbers in content `LEGACIES`): one lasting rule in a hero's
 * passive. Only the class lab turns them on for now (`HeroInit.legacy`); a fight without one has no `s.lg` and none
 * of this runs, so it plays and hashes exactly as before. Nothing here walks every nightmare against every
 * other: the warrior's shields look within their reach once per charge, the shoots look within their radius once a
 * second (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** A bastion wave (the waves' own numbers); every other fight uses the first. */
const inWave = (s: BattleState): boolean => s.mode === 'wave';

/** The one a fight is about: it crosses lines and is never held by a shoot. */
const bossLike = (s: BattleState, e: Enemy): boolean =>
  isBoss(e) || e.un !== undefined || e.mask !== undefined || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || e.kind === 'dragon' || e.id === s.bossId || e.id === s.dungeon?.boss;

/** A nightmare that fights at arm's length on the ground (no flyer, no shooter, no engine). */
const meleeFoe = (e: Enemy): boolean => {
  const d = NIGHTMARES[e.kind];
  return !isFlying(e) && d.reach < 2000 && !d.shootsHeroes && !d.lobs && !d.siege && !d.structureHunter && !d.shootsTowers;
};

// ---------- start ----------

/** Counts what a legacy did (the lab reads it; nothing in the fight does). */
export const tally = (s: BattleState, k: string, v = 1): void => {
  const n = s.lg?.n;
  if (n) n[k] = (n[k] ?? 0) + v;
};

/** The second aura when none is chosen: life next to devotion, else devotion (the best pair, docs/sendas/paladin.md §9). */
export const secondAura = (first: PaladinAura | undefined, chosen?: PaladinAura): PaladinAura => {
  const one = first ?? 'devotion';
  if (chosen && chosen !== one) return chosen;
  return one === 'devotion' ? 'life' : 'devotion';
};

/** The fight begins: the legacies' state, and Tormo beside each artificer carrying it. */
export const legacyStart = (s: BattleState, inits: HeroInit[]): void => {
  s.lg = { sprouts: [], patches: [], n: {} };
  const L = LEGACIES.artificer;
  for (const h of s.heroes) {
    if (!h.lg || h.cls !== 'artificer') continue;
    const init = inits.find((i) => i.pid === h.pid);
    if (!init) continue;
    const ref = reference(init);
    const hp = imax(1, mulBp(ref.v, L.hpBp));
    // Its punch carries `every` ticks' worth of its damage a second.
    const punch = imax(1, mulBp(idiv(mulBp(mulBp(ref.d, L.realBp), L.dpsBp) * L.every, 12), h.stats.powerBp));
    (s.bots ??= []).push({
      id: s.nextId++,
      owner: h.id,
      pid: h.pid,
      kind: 'automaton',
      tormo: true,
      x: h.x + L.behind,
      y: h.y,
      hp,
      maxHp: hp,
      damage: punch,
      radius: L.radius,
      every: L.every,
      cd: L.every,
      expires: 0x7fffffff,
      taunt: 0,
      aura: 0,
      auraBp: 0,
      facing: 0,
      anim: 'idle',
      animT: 0,
    });
  }
};

// ---------- every tick (after the heroes moved, before the nightmares) ----------

export const legacyTick = (s: BattleState): void => {
  for (const h of s.heroes) {
    if (h.light !== undefined) lightTick(s, h);
    if (h.lgWith && h.lgWith > 0) h.lgWith--;
    if (!h.lg || h.downTicks > 0) continue;
    // Ticks unmoved (striking where it stands counts as still).
    if (h.lgX === h.x && h.lgY === h.y) h.lgStill = (h.lgStill ?? 0) + 1;
    else {
      h.lgStill = 0;
      h.lgX = h.x;
      h.lgY = h.y;
    }
    if (h.cls === 'ranger') trace(s, h);
    else if (h.cls === 'barbarian') hunt(s, h);
    else if (h.cls === 'druid') sow(s, h);
  }
  if (s.lg?.sprouts.length) sprouts(s);
  if (s.lg?.patches.length) patches(s);
};

// ---------- warrior: the Company ----------

/**
 * A blow he takes standing still charges the shields; full, they strike the biggest nightmare in front of him (the
 * one with the most life within `reach` ahead, ties the lowest id; the guardian counts) for `hitBp` of his attack.
 */
export const shieldsCharge = (s: BattleState, h: Hero): void => {
  const L = LEGACIES.warrior;
  if ((h.lgStill ?? 0) < L.stillTicks) return;
  h.lgCharge = (h.lgCharge ?? 0) + 1;
  if (h.lgCharge < L.blows) return;
  const a = h.facing;
  let best: Enemy | null = null;
  for (const e of enemiesNear(s, h.x, h.y, L.reach, (o) => targetable(s, o) && o.kind !== 'boneWall')) {
    // In front: ahead of him along where he faces (bp).
    if ((e.x - h.x) * cosA(a) + (e.y - h.y) * sinA(a) < 0) continue;
    if (!best || e.maxHp > best.maxHp || (e.maxHp === best.maxHp && e.id < best.id)) best = e;
  }
  if (!best) return;
  h.lgCharge = 0;
  const dmg = imax(1, mulBp(mulBp(h.stats.attack, h.stats.powerBp), inWave(s) ? L.bastionHitBp : L.hitBp));
  tally(s, 'shieldStrikes');
  tally(s, 'shieldDamage', damageEnemy(s, best, dmg, { pid: h.pid }));
  if (bossLike(s, best)) tally(s, 'shieldOnBoss');
  ev(s, { t: 'boom', x: best.x, y: best.y, r: 700, kind: 'shields' });
};

// ---------- ranger: the whole map ----------

/** The elites and bosses that come into the ranger's reach are traced (until they die). */
const trace = (s: BattleState, h: Hero): void => {
  const r = h.stats.range;
  const r2 = r * r;
  for (const e of s.enemies) {
    if (e.hp <= 0 || (e.lgTr ?? 0) & 1 || dist2(e.x, e.y, h.x, h.y) > r2 || !(bigFoe(e) || bossLike(s, e))) continue;
    e.lgTr = 1;
    e.lgTrAt = s.tick;
  }
};

/** A basic attack that was no critical, on a traced nightmare: one of this hero's first ones on it is. */
export const tracedCrit = (s: BattleState, h: Hero, e: Enemy): boolean => {
  let tr = e.lgTr ?? 0;
  if (!(tr & 1)) return false;
  // Every window the criticals come back.
  if (s.tick - (e.lgTrAt ?? s.tick) >= LEGACIES.ranger.window) {
    tr = 1;
    e.lgTrAt = s.tick;
  }
  const i = s.heroes.indexOf(h);
  if (i < 0 || i > 7) return false;
  const sh = 1 + 3 * i;
  const used = (tr >> sh) & 7;
  if (used >= (inWave(s) ? LEGACIES.ranger.bastionCrits : LEGACIES.ranger.crits)) return false;
  e.lgTr = tr + (1 << sh);
  return true;
};

// ---------- mage: the lesson of the sea ----------

/** The damage type a skill deals (its program's). */
const typeOf = (sk: SkillDef): DamageType => SKILL_CASTS[sk.id].type;

/**
 * A mage's skill of another type than the last, within the window: wider (its radius, a wall's length, a line's
 * reach; a chain, more jumps), the larger of that and what its form, glyph or variant already gave. `base` is the
 * skill at its rank alone.
 */
export const lesson = (s: BattleState, h: Hero, sk: SkillDef, base: SkillDef): SkillDef => {
  const L = LEGACIES.mage;
  if (h.lgType === undefined || h.lgType === typeOf(sk) || s.tick - (h.lgAt ?? -100000) > L.windowTicks) return sk;
  const form = SKILL_CASTS[sk.id].form;
  const cap = inWave(s) ? L.bastionCap : L.cap;
  const wide = inWave(s) ? L.bastionRadiusBp : L.radiusBp;
  if (form === 'wall') return { ...sk, amount: imax(sk.amount, mulBp(base.amount, wide)) };
  if (form === 'chain') return { ...sk, amount: imax(sk.amount, base.amount + L.chainExtra) };
  if (form === 'line') return { ...sk, castRange: imax(sk.castRange, mulBp(base.castRange, wide)) };
  if (sk.radius <= 0) return sk;
  return { ...sk, radius: imax(sk.radius, imin(cap, mulBp(base.radius, wide))) };
};

/** The skill went out: its type and when, for the next one. */
export const lessonCast = (s: BattleState, h: Hero, sk: SkillDef): void => {
  h.lgType = typeOf(sk);
  h.lgAt = s.tick;
};

/** Whether the mage's skill in this slot would come out widened now (the bot alternates types). */
export const lessonReady = (s: BattleState, h: Hero, sk: SkillDef): boolean =>
  h.lgType !== undefined && h.lgType !== typeOf(sk) && s.tick - (h.lgAt ?? -100000) <= LEGACIES.mage.windowTicks;

// ---------- cleric: keeping company ----------

/** An ally falling near a standing cleric with the legacy: it stays as a light instead. True if it does. */
export const lightCatch = (s: BattleState, h: Hero): boolean => {
  const L = LEGACIES.cleric;
  if (h.light !== undefined || s.tick - (h.lightAt ?? -100000) < L.cooldown) return false;
  let c: Hero | null = null;
  let cd = 0x7fffffff;
  for (const o of s.heroes) {
    if (!o.lg || o.cls !== 'cleric' || o.id === h.id || o.downTicks > 0 || o.light !== undefined) continue;
    const d = dist2(o.x, o.y, h.x, h.y);
    if (d < cd) {
      cd = d;
      c = o;
    }
  }
  if (!c) return false;
  const near = cd <= L.radius * L.radius;
  const t = inWave(s) ? L.bastionTicks : L.ticks;
  h.hp = 1;
  h.light = near ? t : 0;
  h.lightWalk = near ? 0 : inWave(s) ? L.bastionTicks : L.walkTicks;
  h.lightBy = c.id;
  h.lightHp = 0;
  h.lightAt = s.tick;
  h.lightPow = h.stats.powerBp;
  h.stats.powerBp = mulBp(h.stats.powerBp, L.powerBp);
  if (h.base) h.base.powerBp = h.stats.powerBp;
  h.taunt = 0;
  h.target = 0;
  h.forced = false;
  h.moving = false;
  h.path = [];
  h.stun = 0;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'light' });
  tally(s, 'lights');
  return true;
};

/** The light ends: its power back. */
const unlight = (h: Hero): void => {
  if (h.lightPow !== undefined) {
    h.stats.powerBp = h.lightPow;
    if (h.base) h.base.powerBp = h.lightPow;
  }
  h.light = undefined;
  h.lightWalk = undefined;
  h.lightBy = undefined;
  h.lightHp = undefined;
  h.lightPow = undefined;
};

/** A light's tick: it walks to its cleric, waits there, and falls if nobody healed it in time. */
const lightTick = (s: BattleState, h: Hero): void => {
  const L = LEGACIES.cleric;
  const c = findHero(s, h.lightBy ?? 0);
  if (!c || c.downTicks > 0) {
    fallNow(s, h);
    return;
  }
  if ((h.lightWalk ?? 0) > 0) {
    if (withinRange(h.x, h.y, c.x, c.y, L.radius)) {
      h.lightWalk = 0;
      h.light = inWave(s) ? L.bastionTicks : L.ticks;
      h.moving = false;
      h.path = [];
      return;
    }
    h.lightWalk = (h.lightWalk ?? 1) - 1;
    if (h.lightWalk <= 0) {
      fallNow(s, h);
      return;
    }
    stepToward(h, c.x, c.y, idiv(h.stats.speed, 12));
    h.ax = h.destX = h.x;
    h.ay = h.destY = h.y;
    return;
  }
  h.light = (h.light ?? 1) - 1;
  if (h.light <= 0) fallNow(s, h);
};

const fallNow = (s: BattleState, h: Hero): void => {
  unlight(h);
  h.hp = 0;
  heroDown(s, h);
};

/** A heal on a light: only its cleric's counts, and enough of it gets it up with that much. Returns what it healed. */
export const lightHeal = (s: BattleState, h: Hero, amount: number, pid: string): number => {
  const c = findHero(s, h.lightBy ?? 0);
  if (!c || c.pid !== pid || amount <= 0) return 0;
  const room = h.stats.maxHp - (h.lightHp ?? 0);
  const got = amount < room ? amount : room;
  if (got <= 0) return 0;
  h.lightHp = (h.lightHp ?? 0) + got;
  if (h.lightHp >= mulBp(h.stats.maxHp, LEGACIES.cleric.healBp)) {
    const hp = h.lightHp;
    unlight(h);
    h.hp = imin(h.stats.maxHp, imax(1, hp));
    // It comes back inspired (the longer counts).
    if ((h.inspire ?? 0) < LEGACIES.cleric.inspireTicks) h.inspire = LEGACIES.cleric.inspireTicks;
    ev(s, { t: 'up', id: h.id });
    tally(s, 'lightRose');
  }
  return got;
};

/** The light in the cleric's reach that most needs it (the bot heals it first). */
export const litAlly = (s: BattleState, h: Hero, r: number): Hero | null => {
  for (const o of s.heroes) if (o.light !== undefined && (o.lightWalk ?? 0) === 0 && o.lightBy === h.id && withinRange(h.x, h.y, o.x, o.y, r)) return o;
  return null;
};

// ---------- barbarian: the long hunt ----------

/** The prey: kept while it stands within reach; when it falls, life and a frenzy; else the next one. */
const hunt = (s: BattleState, h: Hero): void => {
  const L = LEGACIES.barbarian;
  if (h.lgPrey !== undefined) {
    const e = findEnemy(s, h.lgPrey);
    if (!e || e.hp <= 0) {
      h.lgPrey = undefined;
      // The leap is ready again, and the next blow lands as a critical all around.
      const leap = kitOf(h).indexOf('leap');
      if (leap >= 0) h.skillCd[leap as 0 | 1 | 2] = 0;
      h.lgAreaCrit = true;
      ev(s, { t: 'boom', x: h.x, y: h.y, r: 900, kind: 'preyFell' });
      tally(s, 'preyFell');
    } else if (!targetable(s, e) || !withinRange(h.x, h.y, e.x, e.y, L.radius)) h.lgPrey = undefined;
    else return;
  }
  if (s.tick % 3 !== 0) return;
  const r2 = L.radius * L.radius;
  let best: Enemy | null = null;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind === 'boneWall' || dist2(e.x, e.y, h.x, h.y) > r2 || !targetable(s, e)) continue;
    if (!best || e.maxHp > best.maxHp) best = e;
  }
  if (best) h.lgPrey = best.id;
};

/** The hunt's blow: a critical (already counted) that also lands on everything around its target. */
export const huntBlow = (s: BattleState, h: Hero, e: Enemy, dmg: number): void => {
  const L = LEGACIES.barbarian;
  h.lgAreaCrit = undefined;
  const share = mulBp(dmg, L.areaBp);
  for (const o of enemiesNear(s, e.x, e.y, L.areaR, (x) => x.id !== e.id && targetable(s, x))) tally(s, 'huntArea', damageEnemy(s, o, share, { pid: h.pid, crit: true }));
  ev(s, { t: 'boom', x: e.x, y: e.y, r: L.areaR, kind: 'huntBlow' });
};

/** A barbarian's blow on its own prey (bp; 10000 = none). */
export const preyBp = (s: BattleState, e: Enemy, pid: string): number => {
  for (const h of s.heroes) if (h.pid === pid) return h.lg && h.cls === 'barbarian' && h.lgPrey === e.id ? 10000 + LEGACIES.barbarian.preyBp : 10000;
  return 10000;
};

/** The fury on the prey: at its full. */
export const preyFuryBp = (): number => 10000 + BARBARIAN.fury.maxBp;

// ---------- paladin: the inherited oath ----------

/** What a second aura does at its share: devotion lets through this much of a blow. */
export const secondDevotionBp = (rankBp: number): number => 10000 - mulBp(10000 - rankBp, LEGACIES.paladin.secondBp);

/** A share of a second aura's strength. */
export const second = (v: number): number => mulBp(v, LEGACIES.paladin.secondBp);

/** Paladin aura reach (the same for both). */
export const auraReach = (aura: PaladinAura): number => (aura === 'devotion' ? PALADIN.devotion.radius : PALADIN.auras[aura].radius);

// ---------- druid: the grandsire ----------

/** Still for long enough, in a fight, where no shoot of its own stands: a new one (the oldest goes past the cap). */
const sow = (s: BattleState, h: Hero): void => {
  const L = LEGACIES.druid;
  const g = s.lg;
  if (!g || (h.bear ?? 0) > 0 || (h.lgStill ?? 0) < L.stillTicks || s.tick - (h.lgSprAt ?? -100000) < L.every) return;
  const mine = g.sprouts.filter((x) => x.by === h.id);
  if (mine.some((x) => withinRange(x.x, x.y, h.x, h.y, L.radius))) return;
  if (!s.enemies.some((e) => e.hp > 0 && withinRange(e.x, e.y, h.x, h.y, 3 * L.radius))) return;
  const cap = inWave(s) ? L.bastionMax : L.max;
  if (mine.length >= cap) {
    const old = mine[0] as (typeof mine)[number];
    g.sprouts = g.sprouts.filter((x) => x !== old);
  }
  g.sprouts.push({ id: s.nextId++, by: h.id, pid: h.pid, x: h.x, y: h.y, at: s.tick, held: s.tick });
  h.lgSprAt = s.tick;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: L.radius, kind: 'sprout' });
  tally(s, 'sprouts');
};

/**
 * Where the druid's bot (and spectre) goes to grow its next shoot: beside the friend with the most nightmares on it
 * (two or more within `radius`), when a shoot is due. Null: nowhere to go (it fights where it stands).
 */
export const sowSpot = (s: BattleState, h: Hero, leash = 9000): { x: number; y: number } | null => {
  const L = LEGACIES.druid;
  if (!h.lg || (h.bear ?? 0) > 0 || s.tick - (h.lgSprAt ?? -100000) < L.every) return null;
  let best: Hero | null = null;
  let most = 1;
  for (const o of s.heroes) {
    if (o.id === h.id || o.downTicks > 0 || o.light !== undefined || !withinRange(o.x, o.y, h.x, h.y, leash)) continue;
    if (s.lg?.sprouts.some((x) => x.by === h.id && withinRange(x.x, x.y, o.x, o.y, L.radius))) continue;
    let n = 0;
    for (const e of s.enemies) if (e.hp > 0 && !isFlying(e) && withinRange(e.x, e.y, o.x, o.y, L.radius)) n++;
    if (n > most) {
      most = n;
      best = o;
    }
  }
  if (!best) return null;
  // A step behind the friend, on the druid's side.
  const dx = h.x - best.x;
  const dy = h.y - best.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const k = imin(d, 700);
  return { x: best.x + idiv(dx * k, d), y: best.y + idiv(dy * k, d) };
};

/** The shoots: once a second they slow what is in them and heal the heroes; now and then one holds the nearest. */
const sprouts = (s: BattleState): void => {
  const L = LEGACIES.druid;
  const g = s.lg;
  if (!g) return;
  const wave = inWave(s);
  const every = wave ? L.bastionHoldEvery : L.holdEvery;
  const r2 = L.radius * L.radius;
  for (const sp of g.sprouts) {
    if (s.tick - sp.held < every) continue;
    let best: Enemy | null = null;
    let bd = r2 + 1;
    for (const e of s.enemies) {
      if (e.hp <= 0 || isFlying(e) || bossLike(s, e) || !targetable(s, e)) continue;
      const d = dist2(e.x, e.y, sp.x, sp.y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    if (!best) continue;
    sp.held = s.tick;
    const t = best.elite ? mulBp(L.holdTicks, L.eliteHoldBp) : L.holdTicks;
    if (best.stun < t) best.stun = t;
    ev(s, { t: 'boom', x: best.x, y: best.y, r: 500, kind: 'sproutHold' });
    tally(s, 'sproutHolds');
  }
  if (s.tick % 12 !== 0) return;
  const slow = wave ? L.bastionSlowBp : L.slowBp;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (!g.sprouts.some((sp) => dist2(e.x, e.y, sp.x, sp.y) <= r2)) continue;
    if (!isFlying(e)) {
      if (e.slowBp < slow) e.slowBp = slow;
      if (e.slowTicks < 13) e.slowTicks = 13;
    }
    // In a shoot (a boss too), it takes more of every blow.
    e.lgVuln = s.tick + 13;
    tally(s, 'sproutSlowSec');
  }
};

/** Accompanied (the cleric's legacy): what this hero's blows are worth now (bp; 10000 = none). */
export const withBp = (h: Hero): number => ((h.lgWith ?? 0) > 0 ? 10000 + LEGACIES.cleric.withBp : 10000);

/** A legacy cleric's heal on another: it goes accompanied a while. */
export const accompany = (s: BattleState, h: Hero, pid: string): void => {
  if (h.pid === pid) return;
  for (const c of s.heroes) {
    if (c.pid !== pid || !c.lg || c.cls !== 'cleric') continue;
    h.lgWith = LEGACIES.cleric.withTicks;
    tally(s, 'accompanied');
    return;
  }
};

/** What a druid's shoot leaves on what it holds: more of every blow (bp; 10000 = none). */
export const heldTakenBp = (s: BattleState, e: Enemy): number => ((e.lgVuln ?? 0) > s.tick ? 10000 + LEGACIES.druid.takenBp : 10000);

// ---------- mage: the ground a widened skill leaves ----------

/** A widened skill landed: its type stays on the ground there a while. */
export const groundMark = (s: BattleState, h: Hero, type: DamageType, x: number, y: number, dmg: number): void => {
  const L = LEGACIES.mage;
  s.lg?.patches.push({ type, x, y, r: L.groundR, until: s.tick + (inWave(s) ? L.bastionGroundTicks : L.groundTicks), pid: h.pid, dmg: imax(1, mulBp(dmg, L.burnBp)) });
  tally(s, 'groundMarks');
};

/** Once a second the ground acts on what stands in it: fire (and the rest) burns, frost slows, lightning stuns. */
const patches = (s: BattleState): void => {
  const g = s.lg;
  if (!g) return;
  g.patches = g.patches.filter((p) => p.until > s.tick);
  if (s.tick % 12 !== 0) return;
  const L = LEGACIES.mage;
  for (const p of g.patches) {
    for (const e of enemiesNear(s, p.x, p.y, p.r, (o) => targetable(s, o))) {
      if (p.type === 'frost') {
        const sl = slowFor(e, L.frostSlowBp);
        if (e.slowBp < sl) e.slowBp = sl;
        if (e.slowTicks < 13) e.slowTicks = 13;
      } else if (p.type === 'lightning') {
        const t = stunFor(e, L.shockTicks);
        if (t > 0 && e.stun < t) e.stun = t;
      } else tally(s, 'groundDamage', damageEnemy(s, e, p.dmg, { pid: p.pid, type: p.type }));
    }
  }
};

// ---------- artificer: Tormo ----------

/** Tormo's trail of its maker's steps: a point every this far, the last few kept. */
const TRAIL_STEP = 700;
const TRAIL_KEEP = 8;

/** Tormo standing (not kneeling, not broken). */
const tormoUp = (b: Bot): boolean => b.hp > 0 && b.kneel === undefined;

/** Tormo's tick: broken, it kneels and gets up; otherwise it walks with its maker and punches what is around it. */
export const tormoTick = (s: BattleState, b: Bot): void => {
  const L = LEGACIES.artificer;
  b.animT++;
  if (b.hp <= 0 && b.kneel === undefined) {
    b.hp = 0;
    b.kneel = s.tick + L.kneelTicks;
    tally(s, 'tormoBroken');
    b.anim = 'idle';
    ev(s, { t: 'boom', x: b.x, y: b.y, r: 600, kind: 'tormoKneels' });
  }
  if (b.kneel !== undefined) {
    if (s.tick < b.kneel) return;
    b.kneel = undefined;
    b.hp = imax(1, mulBp(b.maxHp, L.riseBp));
    ev(s, { t: 'boom', x: b.x, y: b.y, r: 600, kind: 'tormoRises' });
  }
  const o = findHero(s, b.owner);
  const step = idiv(L.speed, 12);
  if (o && o.downTicks === 0) {
    let best: Enemy | null = null;
    let bd = L.hunt * L.hunt + 1;
    for (const e of s.enemies) {
      if (e.hp <= 0 || isFlying(e) || !targetable(s, e)) continue;
      const d = dist2(e.x, e.y, o.x, o.y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    if (best && !withinRange(b.x, b.y, best.x, best.y, idiv(L.radius * 3, 4))) {
      b.anim = 'walk';
      stepToward(b, best.x, best.y, step);
    } else if (!best) {
      // At its maker's back, by the way its maker walked (round what stands between, as the hero went).
      const trail = (b.trail ??= []);
      const last = trail[trail.length - 1];
      if (!last || !withinRange(last.x, last.y, o.x, o.y, TRAIL_STEP)) {
        trail.push({ x: o.x, y: o.y });
        if (trail.length > TRAIL_KEEP) trail.shift();
      }
      const bx = o.x - idiv(cosA(o.facing) * L.behind, 10000);
      const by = o.y - idiv(sinA(o.facing) * L.behind, 10000);
      if (!withinRange(b.x, b.y, bx, by, 300)) {
        // The oldest step of the trail it hasn't reached yet, while its maker is far; then straight to its back.
        while (trail.length > 1 && withinRange(b.x, b.y, (trail[0] as { x: number }).x, (trail[0] as { y: number }).y, 400)) trail.shift();
        const far = !withinRange(b.x, b.y, o.x, o.y, L.behind + TRAIL_STEP);
        const to = far && trail.length > 1 ? (trail[0] as { x: number; y: number }) : { x: bx, y: by };
        b.anim = 'walk';
        stepToward(b, to.x, to.y, step);
      } else if (b.anim === 'walk') b.anim = 'idle';
    } else if (b.anim === 'walk') b.anim = 'idle';
  }
  if (b.cd > 0) {
    b.cd--;
    if (b.anim === 'attack' && b.animT > 6) b.anim = 'idle';
    return;
  }
  const hits = enemiesNear(s, b.x, b.y, b.radius, (e) => targetable(s, e) && !isFlying(e));
  if (!hits.length) return;
  for (const e of hits) tally(s, 'tormoDamage', damageEnemy(s, e, b.damage, { pid: b.pid }));
  tally(s, 'tormoPunches');
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  ev(s, { t: 'boom', x: b.x, y: b.y, r: b.radius, kind: 'sweep' });
};

/** A nightmare walking past a standing Tormo strikes it on its way (it doesn't stop for it). */
export const tormoPass = (s: BattleState, e: Enemy): void => {
  if (e.cd > 0 || !meleeFoe(e)) return;
  for (const b of s.bots ?? []) {
    if (!b.tormo || !tormoUp(b) || !withinRange(e.x, e.y, b.x, b.y, NIGHTMARES[e.kind].reach + 400)) continue;
    e.cd = e.reload;
    e.anim = 'attack';
    e.animT = 0;
    b.hp -= blowOf(s, e, e.damage);
    tally(s, 'tormoSoaked', blowOf(s, e, e.damage));
    ev(s, { t: 'hit', id: b.id, dmg: e.damage, x: b.x, y: b.y });
    return;
  }
};

/** A wall, gate or tower Tormo stands beside: what it takes (bp; 10000 = none). */
export const shoulderBp = (s: BattleState, st: Structure): number => {
  if (st.type !== 'rampart' && st.type !== 'wall' && st.type !== 'gate' && !firesAsTower(st)) return 10000;
  const { x, y } = hexToWorld(st.q, st.r);
  for (const b of s.bots ?? []) if (b.tormo && tormoUp(b) && withinRange(b.x, b.y, x, y, LEGACIES.artificer.shoulderR)) return LEGACIES.artificer.shoulderBp;
  return 10000;
};
